import { isEmpty } from 'lodash';
import React, { Component, ReactNode, useCallback, useContext, useMemo, useState } from 'react';
import { StyleProp, ViewStyle } from 'react-native';
import { MBView, MBViewPadding } from '@shared/components/MBView';
import { View } from '@shared/components/View';
import { MBIconV2, MBIconV2Name } from '@shared/components/image';
import { MBLoadingIndicator } from '@shared/components/loading';
import { MBTouchableOpacity } from '@shared/components/touchable';
import {
  MBTextBody1,
  MBTextBody1Bold,
  MBTextBody2,
  MBTextBody2Bold,
} from '@shared/components/typography';
import { ColorThemeContext, Colors, SharedStyles, grid } from '@shared/styles';
import { MBAnalytics } from '@shared/util/analytics';
import { useMountedRef } from '@shared/util/hooks';
import { Styles } from './styles';

export type MBButtonTheme = 'normal' | 'transparent' | 'outline' | 'tertiary';

export interface MBButtonProps {
  /*
        Title of button
    */
  title?: string | ReactNode;

  bold?: boolean;
  loading?: boolean;
  /*
        Size of the button. This determines the height of the button.
    */
  size?: 'small' | 'normal';

  /*
        Button type decides the coloring of the button. Default is "normal",
        which makes it red (primary action). "transparent" makes it transparent
    */
  theme?: MBButtonTheme;

  /*
        Override button title text color. Otherwise default values are set depending on theme.
    */
  titleColor?: string;

  /*
        Color of the outline and title when the outline theme is used.
    */
  borderColor?: string;
  backgroundColor?: string;
  /*
        Icons placed to the left/right of the button text
    */
  rightIcon?: MBIconV2Name;
  rightIconSize?: number;
  rightIconColor?: string;
  rightIconStyle?: StyleProp<ViewStyle>;

  leftIcon?: MBIconV2Name;
  leftIconSize?: number;
  leftIconColor?: string;
  leftIconStyle?: StyleProp<ViewStyle>;
  /*
        Callback when the button is pressed.
    */
  onPress?: (node: React.Component) => any;

  /*
        Is the button disabled?
    */
  disabled?: boolean;

  /*
        Make the button wrap its content instead of expanding to the
        full width of the container.
    */
  wrap?: boolean;

  /*
        Default is "normal"
    */
  paddingHorizontal?: MBViewPadding;

  style?: StyleProp<ViewStyle>;
  attributionId?: string;
  testID?: string;
}

export function useBackgroundColor(theme: MBButtonTheme, disabled: boolean): string {
  const colors = useContext(ColorThemeContext);
  if (theme === 'transparent') {
    return 'transparent';
  }

  if (disabled) {
    return 'white';
  }

  if (theme === 'outline') {
    return colors.button.backgroundInverted;
  }

  if (theme === 'tertiary') {
    return colors.button.tertiary;
  }

  return colors.button.background;
}

export function useBorderColor(theme: MBButtonTheme, disabled: boolean): string {
  const colors = useContext(ColorThemeContext);
  if (disabled && theme !== 'transparent') {
    return Colors.v2.gray10;
  }

  if (theme === 'outline') {
    return colors.button.foregroundInverted;
  }

  return 'transparent';
}

export function useForegroundColor(theme: MBButtonTheme, disabled: boolean): string {
  const colors = useContext(ColorThemeContext);
  if (disabled) {
    return Colors.v2.gray30;
  }

  if (theme === 'outline' || theme === 'transparent') {
    return colors.button.foregroundInverted;
  }

  if (theme === 'tertiary') {
    return colors.foregroundActive;
  }

  return colors.button.foreground;
}

export function MBButton({
  title,
  wrap,
  leftIcon,
  leftIconSize = grid(2),
  leftIconColor,
  leftIconStyle = { paddingRight: 5 },
  rightIcon,
  rightIconSize = grid(2),
  rightIconColor,
  rightIconStyle = { paddingRight: 5 },
  onPress,
  bold = true,
  size = 'normal',
  theme = 'normal',
  paddingHorizontal = 2.5,
  attributionId,
  testID,
  titleColor: titleColorOverride,
  borderColor: borderColorOverride,
  backgroundColor: backgroundColorOverride,
  loading: loadingProp,
  disabled: disabledProp,
  style,
}: MBButtonProps) {
  const mounted = useMountedRef();
  const [_loading, setLoading] = useState(false);
  const loading = loadingProp || _loading;
  const disabled = disabledProp || loading;

  const _backgroundColor = useBackgroundColor(theme, disabled);
  const backgroundColor = backgroundColorOverride || _backgroundColor;
  const foregroundColor = useForegroundColor(theme, disabled);
  const titleColor = titleColorOverride || foregroundColor;

  const _borderColor = useBorderColor(theme, disabled);
  const borderColor = borderColorOverride || _borderColor;

  const handlePress = useCallback(
    async (node: Component) => {
      if (!onPress) {
        return;
      }

      if (!!attributionId) {
        MBAnalytics.track('ButtonPress', { attributionId });
      }

      // Start loading if onPress returns promise
      const maybePromise = onPress(node);
      if (maybePromise instanceof Promise) {
        try {
          setLoading(true);
          await maybePromise;
        } finally {
          if (mounted.current) {
            setLoading(false);
          }
        }
      }
    },
    [attributionId, onPress, mounted],
  );

  const TextComponent = (() => {
    if (size === 'small') {
      return bold ? MBTextBody2Bold : MBTextBody2;
    }
    return bold ? MBTextBody1Bold : MBTextBody1;
  })();

  const titleContent = useMemo(() => {
    if (React.isValidElement(title)) {
      return title;
    }

    return (
      <TextComponent color={titleColor} style={[wrap && Styles.wrap]}>
        {title}
      </TextComponent>
    );
  }, [titleColor, title, TextComponent, wrap]);

  const content = (
    <MBView
      center
      paddingHorizontal={paddingHorizontal}
      style={[
        Styles.button,
        size === 'normal' && Styles.buttonSizeNormal,
        size === 'small' && Styles.buttonSizeSmall,
        { backgroundColor, borderColor },
        style,
      ]}
      testID={testID}
    >
      {/* If we're loading, overlay a loading indicator */}
      {loading && (
        <MBView center style={SharedStyles.absoluteCover}>
          <MBLoadingIndicator color={titleColor} />
        </MBView>
      )}

      {/*
        When loading, we make the text content invisible.
        Note: we still want to render it since that makes
        the button keep its shape. Otherwise it might be
        smaller while loading since the loading indicator
        probably is less wide than the text.
      */}
      <View row centerVertically style={{ opacity: loading ? 0 : 1 }}>
        {leftIcon && (
          <MBView style={leftIconStyle}>
            <MBIconV2
              name={leftIcon}
              color={!isEmpty(leftIconColor) ? leftIconColor : titleColor}
              size={leftIconSize}
            />
          </MBView>
        )}
        {titleContent}
        {rightIcon && (
          <MBView style={rightIconStyle}>
            <MBIconV2
              name={rightIcon}
              color={!isEmpty(rightIconColor) ? rightIconColor : titleColor}
              size={rightIconSize}
            />
          </MBView>
        )}
      </View>
    </MBView>
  );

  if (!onPress) {
    return <View>{content}</View>;
  }

  return (
    <MBTouchableOpacity onPress={handlePress} disabled={disabled}>
      {content}
    </MBTouchableOpacity>
  );
}
